import React, { useState, useEffect } from 'react';
import { MacroDisplay } from 'components/MacroDisplay';
import { functions } from 'services/firebaseService';
import { Meal } from '@llm-macro-tracker/shared';
import { httpsCallable } from 'firebase/functions';
import { Dialog } from '@/components/ui/dialog';
import { ImageUploader } from '@/components/ImageUploader';
import DOMPurify from 'dompurify';
import { useUser } from 'contexts/UserContext';
import { InferredIngredientBadge } from '@/components/InferredIngredientBadge';
import { Ingredient } from '@llm-macro-tracker/shared';

interface ExtendedMeal extends Meal {
  imageId?: string;
}

interface AddMealModalProps {
  onClose: () => void;
  onAddOrUpdateMeal: (meal: Omit<ExtendedMeal, 'userId' | 'timestamp' | 'v'>) => Promise<void>;
  selectedDate: Date;
  editingMeal: (ExtendedMeal & { id: string }) | null | undefined;
}

export const AddMealModal: React.FC<AddMealModalProps> = ({
  onClose,
  onAddOrUpdateMeal,
  selectedDate,
  editingMeal,
}) => {
  const { uploadImage } = useUser();
  const [mealInput, setMealInput] = useState('');
  const [editableMealTitle, setEditableMealTitle] = useState('');
  const [pendingMeal, setPendingMeal] = useState<Omit<ExtendedMeal, 'userId' | 'timestamp'> | null>(
    null,
  );
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showTitleInput, setShowTitleInput] = useState(false);
  const [hasContentChanged, setHasContentChanged] = useState(false);
  const [imageId, setImageId] = useState<string | undefined>(editingMeal?.imageId);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    console.log('useEffect triggered. editingMeal:', editingMeal);
    if (editingMeal) {
      console.log('Setting state for editing meal');
      setMealInput(editingMeal.description);
      setEditableMealTitle(editingMeal.mealName);
      setIsEditing(true);
      setShowTitleInput(true);
      setImageId(editingMeal.imageId);
    } else {
      console.log('Resetting state for new meal');
      resetState();
    }
  }, [editingMeal]);

  const resetState = () => {
    console.log('Resetting state');
    setMealInput('');
    setEditableMealTitle('');
    setPendingMeal(null);
    setIsEditing(false);
    setError(null);
    setShowTitleInput(false);
    setHasContentChanged(false);
    setImageId(undefined);
    setIsUploading(false);
  };

  const handleClose = () => {
    console.log('handleClose called. Current state:', { mealInput, editableMealTitle });
    resetState();
    onClose();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    console.log('handleInputChange called. New value:', e.target.value);
    setMealInput(e.target.value);
    setHasContentChanged(true);
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log('handleTitleChange called. New value:', e.target.value);
    setEditableMealTitle(e.target.value);
    setHasContentChanged(true);
  };

  const handleImageUpload = async (file: File) => {
    setIsUploading(true);
    try {
      const uploadedImageId = await uploadImage(file);
      setImageId(uploadedImageId);
      setHasContentChanged(true);
    } catch (error) {
      console.error('Error uploading image:', error);
      setError('Failed to upload image. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    console.log('handleSubmit called. Current state:', { mealInput, editableMealTitle });
    if (!mealInput.trim()) {
      setError('Please enter a meal description');
      return;
    }

    setIsLoading(true);
    setError(null);

    if (!hasContentChanged && pendingMeal) {
      console.log('Only title changed. Updating pending meal.');
      setPendingMeal({
        ...pendingMeal,
        mealName: editableMealTitle,
      });
      setIsEditing(false);
      setIsLoading(false);
      return;
    }

    try {
      console.log('Calling analyzeMeal function');
      const analyzeMeal = httpsCallable(functions, 'analyzeMeal');
      const sanitizedMealInput = DOMPurify.sanitize(mealInput);

      const result = await analyzeMeal({
        mealDescription: sanitizedMealInput,
        imageId: imageId ? imageId.split('/').pop() : undefined,
      });
      console.log('analyzeMeal result:', result);

      const analyzedMeal = result.data as {
        success: boolean;
        mealTitle?: string;
        items?: Ingredient[];
        error?: string;
      };

      if (analyzedMeal.success && analyzedMeal.mealTitle && analyzedMeal.items) {
        const totalMacros = analyzedMeal.items.reduce(
          (acc, item) => ({
            calories: acc.calories + item.calories,
            protein: acc.protein + item.protein,
            carbs: acc.carbs + item.carbs,
            fat: acc.fat + item.fat,
          }),
          { calories: 0, protein: 0, carbs: 0, fat: 0 },
        );

        const newMealTitle = editableMealTitle || analyzedMeal.mealTitle;
        setPendingMeal({
          mealName: newMealTitle,
          description: mealInput,
          totalMacros,
          ingredients: analyzedMeal.items,
          date: selectedDate,
          imageId: imageId || undefined,
          v: 2,
        });
        setEditableMealTitle(newMealTitle);
        setIsEditing(false);
      } else if (analyzedMeal.error) {
        setError(analyzedMeal.error);
      } else {
        throw new Error('Unexpected response from meal analysis');
      }
    } catch (error) {
      console.error('Error processing meal:', error);
      setError('Failed to process meal. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const confirmMeal = async () => {
    console.log('confirmMeal called. Current state:', {
      mealInput,
      editableMealTitle,
      pendingMeal,
    });
    if (pendingMeal) {
      try {
        await onAddOrUpdateMeal({
          ...pendingMeal,
          mealName: editableMealTitle || pendingMeal.mealName,
        });
        console.log('Meal confirmed. Closing modal.');
        handleClose();
      } catch (error) {
        console.error('Error adding/updating meal:', error);
        setError('Failed to add/update meal. Please try again.');
      }
    }
  };

  useEffect(() => {
    console.log('State changed:', { mealInput, editableMealTitle, isEditing, pendingMeal });
  }, [mealInput, editableMealTitle, isEditing, pendingMeal]);

  return (
    <Dialog
      open={true}
      onOpenChange={(open) => {
        console.log('Dialog onOpenChange called. Open:', open);
        if (!open) handleClose();
      }}
    >
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto relative">
          <button
            onClick={handleClose}
            className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
          <h2 className="text-2xl font-bold mb-4 text-gray-800">
            {editingMeal ? 'Edit Meal' : 'Log New Meal'}
          </h2>
          {error && (
            <div
              className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4"
              role="alert"
            >
              <span className="block sm:inline">{error}</span>
            </div>
          )}
          {!pendingMeal || isEditing ? (
            <>
              <div className="mb-4">
                <button
                  onClick={() => setShowTitleInput(!showTitleInput)}
                  className="text-indigo-600 hover:text-indigo-800 text-sm font-medium flex items-center"
                >
                  {showTitleInput ? 'Remove custom title' : 'Add custom title (optional)'}
                </button>
                {showTitleInput && (
                  <input
                    type="text"
                    value={editableMealTitle}
                    onChange={handleTitleChange}
                    className="w-full p-2 mt-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Enter a custom meal title"
                  />
                )}
              </div>
              <textarea
                value={mealInput}
                onChange={handleInputChange}
                placeholder="Describe your meal..."
                className="w-full p-2 border border-gray-300 rounded mb-4 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                rows={4}
              />
              <ImageUploader onImageUpload={handleImageUpload} isUploading={isUploading} />
              <div className="flex justify-end mt-6">
                <button
                  onClick={handleClose}
                  className="bg-gray-200 text-gray-800 px-4 py-2 rounded mr-2 hover:bg-gray-300 transition duration-300"
                  disabled={isLoading || isUploading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="bg-indigo-500 text-white px-4 py-2 rounded hover:bg-indigo-600 transition duration-300"
                  disabled={isLoading || isUploading}
                >
                  {isLoading || isUploading ? 'Processing...' : 'Analyze Meal'}
                </button>
              </div>
            </>
          ) : (
            <>
              <h3 className="font-bold text-xl mb-2 text-gray-800">
                {editableMealTitle || pendingMeal.mealName}
              </h3>
              <p className="text-sm text-gray-600 mb-4">{pendingMeal.description}</p>
              <MacroDisplay macros={pendingMeal.totalMacros} />
              <div className="space-y-2 mt-4">
                {pendingMeal.ingredients.map((ingredient, idx) => (
                  <div key={idx} className="bg-gray-100 p-3 rounded">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-800">{ingredient.name}</span>
                      <InferredIngredientBadge isInferred={ingredient.isInferred} />
                    </div>
                    <div className="grid grid-cols-4 gap-2 text-sm mt-1">
                      <span className="text-orange-700">🔥 {ingredient.calories} cal</span>
                      <span className="text-emerald-700">🥩 {ingredient.protein}g</span>
                      <span className="text-amber-700">🍞 {ingredient.carbs}g</span>
                      <span className="text-sky-700">🥑 {ingredient.fat}g</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex justify-end mt-6">
                <button
                  onClick={handleClose}
                  className="bg-gray-200 text-gray-800 px-4 py-2 rounded mr-2 hover:bg-gray-300 transition duration-300"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setPendingMeal(null);
                    setIsEditing(true);
                  }}
                  className="bg-yellow-400 text-white px-4 py-2 rounded mr-2 hover:bg-yellow-500 transition duration-300"
                >
                  Modify
                </button>
                <button
                  onClick={confirmMeal}
                  className="bg-indigo-500 text-white px-4 py-2 rounded hover:bg-indigo-600 transition duration-300"
                >
                  Save Meal
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </Dialog>
  );
};
